// THE VR LOOK — trees (APP_VRSTYLE.md "Trees"). Every crown of a draw chunk is a solid low-poly crown in place
// of its crossed alpha sprite: a cone (conifer / larch / columnar) or a faceted dome (the broadleaf forms;
// crowns.js CROWN_PROFILE), the crown base at the form's base fraction, a thin trunk below, opaque — so the eye
// MSAA cleans its edges (no alpha cut-out, no A2C coverage dots, no wind sway, no hand-off screen door).
// trees.js calls vrsTreeChunk(forest, group) on a built chunk BEFORE it is shown: each species' InstancedMesh
// keeps its instance matrices (treeMatrix: unit quad width × s·wf, height s, origin 0.3 m below the ground — the
// unit crown geometry here has the same footprint, so the chunk's bounding sphere stays valid) and gets the
// crown geometry + the species' flat material. The hero fans / 3D trunks are not built (trees.js update hook).
//
// Colour = the species colour (region vegetation, vegpaint.js resolveSpecies) × a per-vertex shade from the
// world's baked sun direction (meta.lighting) on the face normal: lit side ~1, shade side ~0.5, the underside
// darker — the same sun the ground's baked light shows. Flat faces (non-indexed): a calm, faceted silhouette.
import * as THREE from 'three';
import { CROWN_PROFILE } from '../crowns.js';
import { vrStyleOn, vrsCap } from './index.js';

const SEG = 5;               // crown facets around (a low-poly silhouette): 10 tris a crown + a 6-tri trunk
const TRUNK_R = 0.035;       // trunk radius in the unit crown (× s·wf)
const TRUNK_COL = new THREE.Color(0x4a3a2c);

// the unit crown of one profile: [positions, normals, trunk flag] non-indexed, flat normals
function crownArrays(kind, base) {
  const P = [], N = [], T = [];
  const tri = (a, b, c, tk) => {
    const ab = new THREE.Vector3().subVectors(b, a), ac = new THREE.Vector3().subVectors(c, a);
    const n = ab.cross(ac).normalize();
    for (const v of [a, b, c]) { P.push(v.x, v.y, v.z); N.push(n.x, n.y, n.z); T.push(tk); }
  };
  const ring = (y, r, rot = 0, n = SEG) => Array.from({ length: n }, (_, i) => {
    const t = (i + rot) / n * Math.PI * 2;
    return new THREE.Vector3(Math.cos(t) * r, y, Math.sin(t) * r);
  });
  const band = (lo, hi, tk = 0) => { const n = lo.length; for (let i = 0; i < n; i++) { const j = (i + 1) % n; tri(lo[i], hi[i], hi[j], tk); tri(lo[i], hi[j], lo[j], tk); } };
  const fan = (pts, apex, up, tk = 0) => { const n = pts.length; for (let i = 0; i < n; i++) { const j = (i + 1) % n; if (up) tri(pts[i], apex, pts[j], tk); else tri(pts[j], apex, pts[i], tk); } };
  // trunk (3 sides): 0 (0.3 m below the ground at s ≈ 10) → into the crown
  band(ring(0, TRUNK_R, 0.5, 3), ring(base + 0.1, TRUNK_R, 0.5, 3), 1);
  if (kind === 'cone') {
    const b = ring(base, 0.5);
    fan(b, new THREE.Vector3(0, 1, 0), true);                       // the sides
    fan(b, new THREE.Vector3(0, base + 0.04, 0), false);            // the underside
  } else {
    const r1 = ring(base + 0.45 * (1 - base), 0.5);                 // a faceted bicone: underside + top
    fan(r1, new THREE.Vector3(0, base, 0), false);
    fan(r1, new THREE.Vector3(0, 1, 0), true);
  }
  return { P: new Float32Array(P), N: new Float32Array(N), T: new Float32Array(T) };
}

const _arrays = new Map();
// a per-chunk geometry over the form's shared arrays (trees.js disposes each chunk geometry: own attributes)
function crownGeometry(form) {
  const prof = CROWN_PROFILE[form] || CROWN_PROFILE.conifer;
  const k = prof[0] + prof[1];
  if (!_arrays.has(k)) _arrays.set(k, crownArrays(prof[0], prof[1]));
  const a = _arrays.get(k);
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.BufferAttribute(a.P, 3));
  g.setAttribute('normal', new THREE.BufferAttribute(a.N, 3));
  g.setAttribute('aTk', new THREE.BufferAttribute(a.T, 1));
  return g;
}

const _sun = { value: new THREE.Vector3(0.26, 0.79, 0.56).normalize() };
const _trunk = { value: TRUNK_COL };
// the world's baked sun (meta.lighting, as sky.js setSun): vrsInit
export function vrsTreeSun(meta) {
  const lt = (meta && meta.lighting) || {};
  if (lt.sun_az_deg == null || lt.sun_alt_deg == null) return;
  const az = (lt.sun_az_deg * Math.PI) / 180, alt = (lt.sun_alt_deg * Math.PI) / 180;
  _sun.value.set(Math.sin(az) * Math.cos(alt), Math.sin(alt), -Math.cos(az) * Math.cos(alt)).normalize();
}

function crownMaterial(hex) {
  const m = new THREE.MeshBasicMaterial({ color: new THREE.Color(hex), fog: true });
  m.onBeforeCompile = (sh) => {
    sh.uniforms.uVsSun = _sun;
    sh.uniforms.uVsTrunk = _trunk;
    sh.vertexShader = sh.vertexShader
      .replace('#include <common>', '#include <common>\nattribute float aTk;\nuniform vec3 uVsSun;\nvarying float vVsShade;\nvarying float vVsTk;')
      .replace('#include <begin_vertex>', `#include <begin_vertex>
  {
    vec3 vsN = normal;
#ifdef USE_INSTANCING
    mat3 vsM = mat3(instanceMatrix);
    vsN = vsM * (normal / vec3(dot(vsM[0], vsM[0]), dot(vsM[1], vsM[1]), dot(vsM[2], vsM[2])));
#endif
    vsN = normalize(mat3(modelMatrix) * vsN);
    vVsShade = 0.5 + 0.5 * max(dot(vsN, uVsSun), 0.0) + 0.08 * vsN.y;
    vVsTk = aTk;
  }`);
    sh.fragmentShader = sh.fragmentShader
      .replace('#include <common>', '#include <common>\nuniform vec3 uVsTrunk;\nvarying float vVsShade;\nvarying float vVsTk;')
      .replace('#include <color_fragment>', '#include <color_fragment>\n  diffuseColor.rgb = mix(diffuseColor.rgb, uVsTrunk, vVsTk) * vVsShade;');
  };
  m.customProgramCacheKey = () => 'vrs-crown1';
  return m;
}

// the species' flat materials, rebuilt when the forest's species set is (forest.mats is replaced then)
function crownMats(forest) {
  if (forest._vrsFor !== forest.mats) {
    if (forest._vrsMats) for (const m of forest._vrsMats) m.dispose();
    forest._vrsMats = forest.species.map((s) => crownMaterial(s.color));
    forest._vrsFor = forest.mats;
  }
  return forest._vrsMats;
}

// trees.js _chunkJob: the built chunk group, before it is added to the scene
export function vrsTreeChunk(forest, group) {
  if (!vrStyleOn()) return;
  const mats = crownMats(forest);
  for (const m of group.children) {
    if (!m.isInstancedMesh) continue;
    const sp = forest.mats.indexOf(m.material);
    if (sp < 0) continue;
    const old = m.geometry;
    m.geometry = crownGeometry(forest.species[sp].form);
    old.dispose();
    m.material = mats[sp];
    m.onBeforeRender = THREE.Object3D.prototype.onBeforeRender;   // no canopy lookup to sync
    m.userData.vrs = 1;
  }
}

// trees.js update: the hero fans + 3D trunks are not built under the style (the crowns carry their trunk)
export function vrsNoHero() { return vrStyleOn(); }

// trees.js _buildR: the chunk build radius under the style ends at the void (vrsD; fog.js — nothing past D is drawn)
export function vrsTreeR(r) { return vrsCap(r); }
